import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { observer } from 'mobx-react-lite';
import { CellContext, flexRender } from '@tanstack/react-table';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';

import { getModifier } from '../../../core/services/event.service';
import { useSelection } from '../../../core/contexts/selection.context';
import { AddStripMenu } from '../../schedule/components/add-strip-menu';
import { Shot, Stripboard } from '../../../app/entities';
import styles from '../stripboard.module.scss';

import type { StripProps } from './stripboard-table';
import { UpdateShotsInput } from '../../../app/entities/shot';
import { bulkUpdateStore } from '../../../core/stores/bulk-update';

interface ShotCellContext<T> extends CellContext<T, unknown> {
  handleEdit?: (data: any) => void;
}

interface ShotStripProps extends StripProps {
  stripboard: Stripboard;
}
export const ShotStripComponent: React.FC<ShotStripProps> = observer(
  ({ row, dayId, stripboard }) => {
    const strip = row.original;
    const { elements: selectedStripsIds, select } = useSelection();

    const selectedShotIds = stripboard?.strips
      .filter((el) => el.type === 'shot' && selectedStripsIds.includes(el._id))
      .map((el) => el.data.shotId);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting } =
      useSortable({
        id: strip?._id,
        data: {
          dayId,
          type: 'row',
          row: strip,
        },
      });

    if (!strip) return null;

    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return null;
    }

    const cells = row.getVisibleCells();
    const isBulkUpdate = selectedShotIds && selectedShotIds.length > 1;

    const isSelected = selectedStripsIds?.includes(strip._id);
    const isHidden = strip.data.isHidden;

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isSorting && isSelected ? 0.5 : 1,
    };

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const modifier = getModifier(e.nativeEvent);

      select?.(strip._id, modifier);
    };

    const handleSingleEdit = (data: UpdateShotsInput) => {
      if (data.tagsIds) {
        shot?.updateTags(data.tagsIds);
        return;
      }

      shot && shot.update(data!);
    };

    const handleEdit = (data: any) => {
      if (!isBulkUpdate) {
        return handleSingleEdit(data);
      }

      const { tagsIds, ...rest } = data;

      bulkUpdateStore.handleShotsBulkUpdate(rest, selectedShotIds, shot!);
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${styles.shotStrip} ${isSelected ? styles.selected : ''}`}
        onClick={handleClick}
        data-dragged={isDragging}
        data-isHidden={isHidden}
      >
        <span className={styles.dragHandle} {...attributes} {...listeners}>
          <DragHandleDots2Icon color="var(--color-surfaces-bg-elevation-5)" />
        </span>

        {cells?.map((cell) => (
          <div key={cell.id} className={styles.cell} style={{ width: cell.column.getSize() }}>
            {flexRender(cell.column.columnDef.cell, {
              ...cell.getContext(),
              handleEdit,
            } as ShotCellContext<any>)}
          </div>
        ))}

        <div className={styles.actionButton}>
          <AddStripMenu
            stripId={strip._id}
            projectId={shot.projectId}
            position={strip.position}
            shot={shot}
            stripType={strip.type}
          />
        </div>
      </div>
    );
  },
);

export const ShotStrip = React.memo(ShotStripComponent);
